import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, Link } from "react-router-dom";
import { CopyGuard } from "@/components/public/CopyGuard";
import { ThemeProvider } from "next-themes";
import Vault from "@/pages/Vault";
import Landing from "@/pages/Landing";
import Welcome from "@/pages/Welcome";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Tour from "@/pages/Tour";
import Trust from "@/pages/Trust";
import DataResidency from "@/pages/DataResidency";
import ExecutorMode from "@/pages/ExecutorMode";
import Emergency from "@/pages/Emergency";
import ExecutorLanding from "@/pages/ExecutorLanding";
import Learn from "@/pages/Learn";
import States from "@/pages/States";
import Faq from "@/pages/Faq";
import Compare from "@/pages/Compare";
import Switch from "@/pages/Switch";
import ForAdvisors from "@/pages/ForAdvisors";
import ForEmployers from "@/pages/ForEmployers";
import Blog from "@/pages/Blog";
import Stories from "@/pages/Stories";
import AdvisorPortal from "@/pages/AdvisorPortal";
import AdvisorCompare from "@/pages/AdvisorCompare";
import ConfirmReceipt from "@/pages/ConfirmReceipt";
import FaxLog from "@/pages/FaxLog";
import PromoLanding from "@/pages/PromoLanding";
import NotFound from "@/pages/NotFound";
import { Navbar } from "@/components/Navbar";
import EmergencyView from "@/pages/EmergencyView";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import JoinVault from "@/pages/JoinVault";
import ResetPin from "@/pages/ResetPin";
import ShareBundle from "@/pages/ShareBundle";
import Listen from "@/pages/Listen";
const KitShare = lazy(() => import("@/pages/KitShare"));
const EmergencyKitShare = lazy(() => import("@/pages/EmergencyKitShare"));
import Memorial from "@/pages/Memorial";
import EnterHousehold from "@/pages/EnterHousehold";
import AdvisorLogin from "@/pages/advisor/AdvisorLogin";
import AdvisorVerify from "@/pages/advisor/AdvisorVerify";
import { AdvisorProvider } from "@/context/AdvisorContext";
import { CommandPalette, useCommandPalette } from "@/components/CommandPalette";
import { OfflineBanner } from "@/components/OfflineBanner";
import { SiteBanner, AdvisorNotice } from "@/components/SiteBanner";
import { SetPinPrompt } from "@/components/SetPinPrompt";
import { hideSplash, styleStatusBar } from "@/lib/native";
import { AskVaultWidget } from "@/components/AskVaultWidget";
import { SecurityProvider, useSecurity } from "@/context/SecurityContext";
import { HelpLauncher } from "@/components/HelpLauncher";
import { LockScreen } from "@/components/LockScreen";
import { BetaFeedback } from "@/components/BetaFeedback";
import { LangProvider } from "@/lib/i18n";
import { NoIndex } from "@/components/Seo";
import { useQuery } from "@tanstack/react-query";
import { siteVisibility } from "@/lib/api";
const PrivateBeta = lazy(() => import("@/pages/PrivateBeta"));
import { usePageview } from "@/hooks/usePageview";
import { AuthCallback } from "@/components/GoogleAuth";
import { AccessibilityViewer } from "@/components/AccessibilityViewer";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Heavy, members-only pages load on demand so public pages ship a small bundle.
const EstatePlanning = lazy(() => import("@/pages/EstatePlanning"));
const AlbumCover = lazy(() => import("@/pages/AlbumCover"));
const GiftAlbum = lazy(() => import("@/pages/GiftAlbum"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const LifeAdmin = lazy(() => import("@/pages/LifeAdmin"));
const Memories = lazy(() => import("@/pages/Memories"));
const Security = lazy(() => import("@/pages/Security"));
const ExecutorPreview = lazy(() => import("@/pages/ExecutorPreview"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Reports = lazy(() => import("@/pages/Reports"));
const TaxPack = lazy(() => import("@/pages/TaxPack"));
const EstateTax = lazy(() => import("@/pages/EstateTax"));
const MarketingKit = lazy(() => import("@/pages/MarketingKit"));
const PasswordHealth = lazy(() => import("@/pages/PasswordHealth"));
const BereavementPack = lazy(() => import("@/pages/BereavementPack"));
const TributeVideo = lazy(() => import("@/pages/TributeVideo"));
const Library = lazy(() => import("@/pages/Library"));
const DocumentBuilder = lazy(() => import("@/pages/DocumentBuilder"));
const EstateDrafts = lazy(() => import("@/pages/EstateDrafts"));
const Accounts = lazy(() => import("@/pages/Accounts"));
const Checkup = lazy(() => import("@/pages/Checkup"));
const Guide = lazy(() => import("@/pages/Guide"));
const BrandBoard = lazy(() => import("@/pages/BrandBoard"));
const Help = lazy(() => import("@/pages/Help"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminHouseholds = lazy(() => import("@/pages/AdminHouseholds"));
const AdminStateRules = lazy(() => import("@/pages/AdminStateRules"));
const AdminCompliance = lazy(() => import("@/pages/AdminCompliance"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const ElderSafety = lazy(() => import("@/pages/ElderSafety"));
const ConciergePrep = lazy(() => import("@/pages/ConciergePrep"));
const ESign = lazy(() => import("@/pages/ESign"));
const SignDocument = lazy(() => import("@/pages/SignDocument"));
const SharedTemplate = lazy(() => import("@/pages/SharedTemplate"));
const GuestCode = lazy(() => import("@/pages/GuestCode"));
const VerifyIdentity = lazy(() => import("@/pages/VerifyIdentity"));
const AgentHome = lazy(() => import("@/pages/AgentHome"));
const ShareCard = lazy(() => import("@/pages/ShareCard"));
const Audit = lazy(() => import("@/pages/Audit"));
const Policies = lazy(() => import("@/pages/Policies"));
const Support = lazy(() => import("@/pages/Support"));
const SupportTicket = lazy(() => import("@/pages/SupportTicket"));
const Status = lazy(() => import("@/pages/Status"));
const Roadmap = lazy(() => import("@/pages/Roadmap"));
const Press = lazy(() => import("@/pages/Press"));
const Alternative = lazy(() => import("@/pages/Alternative"));
const PartnerLanding = lazy(() => import("@/pages/PartnerLanding"));
const PartnerStats = lazy(() => import("@/pages/PartnerStats"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const VideoShare = lazy(() => import("@/pages/VideoShare"));
const LetterShare = lazy(() => import("@/pages/LetterShare"));
const SocialChannel = lazy(() => import("@/pages/SocialChannel"));
const AdvisorDashboard = lazy(() => import("@/pages/advisor/AdvisorDashboard"));

const VAULT_PATHS = ["/agent", "/vault", "/planning", "/life-admin", "/memories", "/pricing", "/reports", "/tax-pack", "/estate-tax", "/marketing-kit", "/passwords", "/bereavement", "/tribute", "/library", "/accounts", "/checkup", "/guide", "/help", "/admin", "/brand", "/audit", "/inventory", "/elder-safety", "/concierge-prep", "/esign", "/verify-identity", "/policies", "/executor-mode", "/executor-preview", "/emergency"];
const Fallback = () => <div className="min-h-[40vh] grid place-items-center text-sm text-muted-foreground" data-testid="route-loading">Loading…</div>;

function LockGate({ children }) {
  const { status, unlocked, member } = useSecurity();
  if (status === null) return null; // still checking
  if (status.pin_set && !unlocked && window.location.pathname === "/pricing" && !window.location.search) return <Navigate to="/#pricing" replace />;
  const agent = unlocked && ["health_agent", "finance_agent"].includes(member?.role);
  if (agent && !["/agent", "/verify-identity"].includes(window.location.pathname)) return <Navigate to="/agent" replace />;
  return <LangProvider member={unlocked ? member : null}>{status.pin_set && !unlocked ? <LockScreen /> : <>{children}{unlocked && !agent && <BetaFeedback />}</>}<HelpLauncher /></LangProvider>;
}

function PaletteHost() {
  const [open, setOpen] = useCommandPalette();
  return <CommandPalette open={open} onOpenChange={setOpen} />;
}

// The assistant follows members to every vault page; /vault and /bereavement mount their own copy with inline doc opening.
function ChatHost() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { member } = useSecurity();
  if (pathname === "/vault" || pathname === "/bereavement" || ["health_agent", "finance_agent"].includes(member?.role)) return null;
  return <AskVaultWidget onOpenDoc={(id) => navigate(`/vault?doc=${id}`)} />;
}

function VaultNotFound() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="vault-not-found">
      <Navbar />
      <main className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="text-[10px] uppercase tracking-widest font-mono text-primary">404</div>
        <h1 className="font-serif text-4xl mt-2">This page isn't in the vault.</h1>
        <p className="mt-3 text-muted-foreground">The link may be old or mistyped. Everything you've filed is still safe.</p>
        <div className="mt-6 flex justify-center gap-2">
          <Link to="/vault" className="rounded-full bg-primary text-primary-foreground px-5 h-10 inline-flex items-center text-sm font-medium" data-testid="vault-not-found-home">Back to the vault</Link>
          <Link to="/help" className="rounded-full border border-border px-5 h-10 inline-flex items-center text-sm" data-testid="vault-not-found-help">Help</Link>
        </div>
      </main>
    </div>
  );
}

function VaultApp() {
  const { pathname } = useLocation();
  if (!VAULT_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"))) return <NotFound />;
  return (
    <SecurityProvider>
      <NoIndex />
      <LockGate>
        <PaletteHost />
        <ChatHost />
        <OfflineBanner />
        <SiteBanner place="app" />
        <AdvisorNotice />
        <SetPinPrompt />
        <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/vault" element={<Vault />} />
          <Route path="/planning" element={<EstatePlanning />} />
          <Route path="/planning/album-cover" element={<AlbumCover />} />
          <Route path="/executor-preview" element={<ExecutorPreview />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/life-admin" element={<LifeAdmin />} />
          <Route path="/memories" element={<Memories />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/tax-pack" element={<TaxPack />} />
          <Route path="/estate-tax" element={<EstateTax />} />
          <Route path="/marketing-kit" element={<MarketingKit />} />
          <Route path="/passwords/health" element={<PasswordHealth />} />
          <Route path="/bereavement" element={<BereavementPack />} />
          <Route path="/executor-mode" element={<ExecutorMode />} />
          <Route path="/emergency" element={<Emergency />} />
          <Route path="/tribute" element={<TributeVideo />} />
          <Route path="/library" element={<Library />} />
          <Route path="/library/fax-log" element={<FaxLog />} />
          <Route path="/library/build/:templateId" element={<DocumentBuilder />} />
          <Route path="/library/drafts" element={<EstateDrafts />} />
          <Route path="/library/drafts/:kind" element={<EstateDrafts />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/checkup" element={<Checkup />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/brand" element={<BrandBoard />} />
          <Route path="/help" element={<Help />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/households" element={<AdminHouseholds />} />
          <Route path="/admin/state-signing-rules" element={<AdminStateRules />} />
          <Route path="/admin/compliance" element={<AdminCompliance />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/elder-safety" element={<ElderSafety />} />
          <Route path="/concierge-prep" element={<ConciergePrep />} />
            <Route path="/esign" element={<ESign />} />
          <Route path="/verify-identity" element={<VerifyIdentity />} />
          <Route path="/agent" element={<AgentHome />} />
          <Route path="*" element={<VaultNotFound />} />
        </Routes>
        </Suspense>
      </LockGate>
    </SecurityProvider>
  );
}

// Marketing routes that close in Private beta. Everything else (vault, admin, invites, support tickets, shares, advisor login, webhooks) stays open.
const CLOSED_IN_PRIVATE = ["/", "/welcome", "/tour", "/trust", "/security", "/how-it-works", "/executor", "/learn", "/states", "/faq", "/compare", "/switch", "/for-advisors", "/blog", "/stories", "/social"];
// Owner/Administrator preview of the gated public site; visitors still get the private-beta page.
const PreviewBanner = () => (
  <div className="fixed bottom-4 right-4 z-[60] max-w-[calc(100vw-6rem)] rounded-full bg-foreground text-background px-4 py-2 text-xs shadow-lg flex items-center gap-3" data-testid="beta-preview-banner">
    <span>Owner preview — visitors currently see the private-beta page</span>
    <a href="/admin" className="underline underline-offset-2" data-testid="beta-preview-admin-link">Site visibility</a>
  </div>
);
function PublicGate({ children }) {
  const { pathname, search } = useLocation();
  const beta = new URLSearchParams(search).get("beta");
  if (beta) sessionStorage.setItem("hv_beta_code", beta.toUpperCase());
  const hasCode = !!(beta || sessionStorage.getItem("hv_beta_code"));
  const closable = CLOSED_IN_PRIVATE.some((p) => pathname === p || (p !== "/" && pathname.startsWith(p + "/"))) && !(hasCode && (pathname === "/welcome" || pathname === "/for-advisors"));
  const { data, isLoading } = useQuery({ queryKey: ["site-visibility"], queryFn: siteVisibility, staleTime: 60_000, retry: 1, enabled: closable, refetchInterval: (q) => (q.state.data?.launch_at ? 60_000 : false) });
  if (!closable) return children;
  if (isLoading) return <Fallback />;
  if (data?.mode === "private" && data.preview && !new URLSearchParams(search).has("gate")) return <>{children}<PreviewBanner /></>;
  if (data?.mode === "private") return <Suspense fallback={<Fallback />}><PrivateBeta settings={data} /></Suspense>;
  return children;
}

// Route table without a router so the prerender script can wrap it in a StaticRouter.
export function AppRoutes() {
  usePageview();
  const location = useLocation();
  // Google sign-in returns to {origin}{path}#session_id=… — exchange it before any route renders.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <LangProvider scope="public">
      <div className="App">
          <a href="#main" data-testid="skip-to-content" className="hv-skip-link" onClick={(e) => { const m = document.querySelector("main"); if (m) { e.preventDefault(); m.setAttribute("tabindex", "-1"); m.focus({ preventScroll: false }); } }}>Skip to content</a>
          <CopyGuard vaultPaths={VAULT_PATHS} />
          <AccessibilityViewer />
          <HelpLauncher vaultPaths={VAULT_PATHS} />
          <PublicGate>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/tour" element={<Tour />} />
            <Route path="/trust" element={<Trust />} />
            <Route path="/data-residency" element={<DataResidency />} />
            <Route path="/security" element={<Security />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/executor" element={<ExecutorLanding />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/learn/:slug" element={<Learn />} />
            <Route path="/states" element={<States />} />
            <Route path="/states/:slug" element={<States />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/support" element={<Support />} />
            <Route path="/support/t/:token" element={<SupportTicket />} />
            <Route path="/status" element={<Status />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/press" element={<Press />} />
          <Route path="/alternatives/:slug" element={<Alternative />} />
          <Route path="/partners/:slug" element={<PartnerLanding />} />
          <Route path="/partner-stats/:key" element={<PartnerStats />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/v/:id" element={<VideoShare />} />
            <Route path="/letter/:token" element={<LetterShare />} />
            <Route path="/social/:channel" element={<SocialChannel />} />
            <Route path="/social" element={<SocialChannel />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/compare/:slug" element={<Compare />} />
            <Route path="/switch" element={<Switch />} />
            <Route path="/switch/:slug" element={<Switch />} />
            <Route path="/for-advisors" element={<ForAdvisors />} />
            <Route path="/employers" element={<ForEmployers />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/preview/:token" element={<Blog preview />} />
            <Route path="/blog/:slug" element={<Blog />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/stories/:slug" element={<Stories />} />
            <Route path="/a/:slug" element={<AdvisorPortal />} />
            <Route path="/a/:slug/compare" element={<AdvisorCompare />} />
            <Route path="/r/:code" element={<PromoLanding />} />
            <Route path="/emergency/:linkId" element={<EmergencyView />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/join/:token" element={<JoinVault />} />
            <Route path="/reset-pin/:token" element={<ResetPin />} />
            <Route path="/share/:linkId" element={<ShareBundle />} />
            <Route path="/sign/:token" element={<Suspense fallback={<Fallback />}><SignDocument /></Suspense>} />
            <Route path="/template/:token" element={<Suspense fallback={<Fallback />}><SharedTemplate /></Suspense>} />
            <Route path="/guest-code/:token" element={<Suspense fallback={<Fallback />}><GuestCode /></Suspense>} />
            <Route path="/confirm/:token" element={<ConfirmReceipt />} />
            <Route path="/listen/:linkId" element={<Listen />} />
            <Route path="/gift/:linkId" element={<Suspense fallback={<Fallback />}><GiftAlbum /></Suspense>} />
            <Route path="/kit/:token" element={<KitShare />} />
            <Route path="/emergency-kit/:token" element={<Suspense fallback={<Fallback />}><EmergencyKitShare /></Suspense>} />
            <Route path="/memorial/:linkId" element={<Memorial />} />
            <Route path="/r/:token" element={<ShareCard />} />
            <Route path="/h/:slug" element={<EnterHousehold />} />
            <Route path="/advisor/*" element={<AdvisorProvider><Routes><Route path="login" element={<AdvisorLogin />} /><Route path="verify/:token" element={<AdvisorVerify />} /><Route path="*" element={<Suspense fallback={<Fallback />}><AdvisorDashboard /></Suspense>} /></Routes></AdvisorProvider>} />
            <Route path="/*" element={<VaultApp />} />
          </Routes>
          </PublicGate>
      </div>
      </LangProvider>
    </ThemeProvider>
  );
}

function App() {
  useEffect(() => {
    hideSplash();
    styleStatusBar(document.documentElement.classList.contains("dark"));
    const obs = new MutationObserver(() => styleStatusBar(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;

